import { useState, useCallback } from 'react';
import PropTypes from 'prop-types';

import {
  Avatar,
  Badge,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Popover,
  Box,
} from '@mui/material';

import { Menu, MenuOpen, NotificationsOutlined as NotificationsIcon } from '@mui/icons-material';

import { useAuth } from 'auth';
import theme from 'theme';

import { NotificationList } from '../NotificationList';
import { SettingsMenu } from '../SettingsMenu';

const Topbar = ({ className, title, onToggleSidebar, isSidebarOpen, sx }) => {
  const auth = useAuth();
  const profileImageUrl = auth.user.photoURL || '/images/avatar-blank.png';

  // const { keycloak } = useAuthContext();
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  // const onLogout = useCallback(() => keycloak.logout('/'), [keycloak]);
  const handleMenu = useCallback(e => setAnchorEl(e.currentTarget), []);
  const handleClose = useCallback(() => setAnchorEl(null), []);

  // const [globalState, dispatch] = useGlobalState();
  const [notificationsEl, setNotificationsEl] = useState(null);
  const showNotifications = Boolean(notificationsEl);

  const handleShowNotifications = useCallback(event => setNotificationsEl(event.currentTarget), []);
  const handleCloseNotifications = useCallback(() => setNotificationsEl(null), []);

  const { notifications } = auth.user;
  const count = notifications.length;

  return (
    <Box
      sx={[
        { flexGrow: 1, display: 'flex', alignItems: 'center' },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      className={className}
    >
      <AppBar
        position="static"
        sx={{
          backgroundColor: theme.palette.common.white,
          boxShadow: theme.custom.shadows.navbar,
          zIndex: theme.zIndex.appBar,
          flexGrow: 1,
          height: '64px',
        }}
        elevation={0}
      >
        <Toolbar>
          <IconButton
            edge="start"
            sx={{ marginRight: theme.spacing(2) }}
            aria-label="Menu"
            onClick={onToggleSidebar}
            data-cy="sidebarToggle"
            size="large"
          >
            {isSidebarOpen ? <MenuOpen /> : <Menu />}
          </IconButton>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            {title}
          </Typography>
          <IconButton
            onClick={handleShowNotifications}
            size="large"
            aria-label="Open notifications"
            aria-haspopup="menu"
            aria-expanded={showNotifications}
          >
            <Badge
              sx={{ top: 3, right: 3 }}
              badgeContent={count}
              color="primary"
              variant="dot"
              overlap="rectangular"
            >
              <NotificationsIcon />
            </Badge>
          </IconButton>
          <div>
            <IconButton
              onClick={handleMenu}
              size="large"
              aria-label="Open user menu"
              aria-haspopup="menu"
              aria-expanded={open}
            >
              <Avatar alt="avatar" src={profileImageUrl} style={{ borderRadius: 10 }} />
            </IconButton>
          </div>
        </Toolbar>
        <Popover
          anchorEl={notificationsEl}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          onClose={handleCloseNotifications}
          open={showNotifications}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
        >
          <NotificationList
            user={auth.user}
            notifications={notifications}
            onSelect={handleCloseNotifications}
          />
        </Popover>
        <Popover
          anchorEl={anchorEl}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          onClose={handleClose}
          open={open}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
        >
          <SettingsMenu />
        </Popover>
      </AppBar>
    </Box>
  );
};

Topbar.propTypes = {
  className: PropTypes.string,
  onToggleSidebar: PropTypes.func,
  isSidebarOpen: PropTypes.bool,
  title: PropTypes.string,
  sx: PropTypes.oneOfType([PropTypes.object, PropTypes.array, PropTypes.func]),
};

Topbar.defaultProps = {
  className: 'topbar',
  title: '',
  onToggleSidebar: () => {},
  sx: undefined,
};

export default Topbar;
